/**
 * headlight-qrcode — the shared, DOM-free core of HeadLight QR handling.
 *
 * Used by browser apps (through headlight-qrcode-web), the links server and API services, so a label
 * printed anywhere is decoded the same way everywhere.
 */

const libFableServiceProviderBase = require('fable-serviceproviderbase');

const libHosts = require('./Headlight-QRCode-Hosts.js');
const libProfiles = require('./Headlight-QRCode-Profiles.js');
const EntityRegistry = require('./Headlight-QRCode-EntityRegistry.js');
const QRCodeCodec = require('./Headlight-QRCode-Codec.js');
const QRCodeMinter = require('./Headlight-QRCode-Minter.js');

/**
 * Fable service wrapping the codec, minter and profile rules for one customer context.
 */
class HeadlightQRCode extends libFableServiceProviderBase
{
	/**
	 * @param {import('fable')} pFable
	 * @param {Object} [pOptions]
	 * @param {Record<string, Object>} [pOptions.Entities] - Registry overrides.
	 * @param {Object} [pOptions.Configuration] - The customer's QR configuration (`{ Profile, CodeFormat, Envelope, SiteManager }`).
	 * @param {Object} [pOptions.CustomProperties] - The customer's CustomProperties.
	 * @param {number|string} [pOptions.IDCustomer]
	 * @param {string} [pOptions.Environment] - Environment name for printed links; defaults to Production.
	 * @param {(pCode: string) => Promise<boolean>} [pOptions.IsTaken]
	 * @param {string} [pServiceHash]
	 */
	constructor(pFable, pOptions, pServiceHash)
	{
		super(pFable, pOptions, pServiceHash);

		this.serviceType = 'HeadlightQRCode';

		const tmpOptions = pOptions || {};
		this.registry = new EntityRegistry(tmpOptions.Entities);
		this.codec = new QRCodeCodec({ Registry: this.registry });
		this.minter = new QRCodeMinter({ IsTaken: tmpOptions.IsTaken });
		this.environment = tmpOptions.Environment || 'Production';
		this.setCustomerConfiguration(tmpOptions.Configuration, tmpOptions.CustomProperties, tmpOptions.IDCustomer);
	}

	/**
	 * @param {Object} [pConfiguration]
	 * @param {Object} [pCustomProperties]
	 * @param {number|string} [pIDCustomer] - Picks the default when nothing is configured.
	 */
	setCustomerConfiguration(pConfiguration, pCustomProperties, pIDCustomer)
	{
		/** @type {import('./Headlight-QRCode-Profiles.js').QRProfile} */
		this.profile = libProfiles.resolveProfile(pConfiguration, pCustomProperties, { IDCustomer: pIDCustomer });
	}

	/**
	 * @param {any} pText
	 * @return {import('./Headlight-QRCode-Codec.js').Decoded}
	 */
	decode(pText)
	{
		return this.codec.decode(pText);
	}

	/**
	 * Encode a label payload using the customer's envelope unless one is given.
	 *
	 * @param {string} pEntity
	 * @param {string} pValue
	 * @param {Object} [pOptions] - `{ Key, Envelope, Environment, Uppercase }`
	 * @return {string}
	 */
	encode(pEntity, pValue, pOptions)
	{
		const tmpOptions = Object.assign({ Envelope: this.profile.Envelope, Environment: this.environment }, pOptions || {});
		return this.codec.encode({ Entity: pEntity, Value: pValue, Key: tmpOptions.Key }, tmpOptions);
	}

	/**
	 * @param {string} pWorkflow
	 * @param {string} pVerb
	 * @param {Array<string>} [pArguments]
	 * @return {string}
	 */
	encodeAction(pWorkflow, pVerb, pArguments)
	{
		return this.codec.encodeAction(pWorkflow, pVerb, pArguments);
	}

	/**
	 * Mint a code in the customer's format.
	 *
	 * @param {import('./Headlight-QRCode-Minter.js').MintContext} pContext
	 * @return {string}
	 */
	mint(pContext)
	{
		return this.minter.mint(this.profile.CodeFormat, pContext, this.profile.SiteManager);
	}

	/**
	 * @param {import('./Headlight-QRCode-Minter.js').MintContext} pContext
	 * @return {Promise<string>}
	 */
	mintAsync(pContext)
	{
		return this.minter.mintAsync(this.profile.CodeFormat, pContext, this.profile.SiteManager);
	}
}

module.exports = HeadlightQRCode;
module.exports.default_configuration = {};
module.exports.Hosts = libHosts;
module.exports.Profiles = libProfiles;
module.exports.EntityRegistry = EntityRegistry;
module.exports.QRCodeCodec = QRCodeCodec;
module.exports.QRCodeMinter = QRCodeMinter;
